import { useSelector } from 'react-redux';
import { startCase } from 'lodash';
import {
  Citations,
  OnboardingChrome,
  citations,
  LikelihoodSlideOutContent,
  LtcAtAgeSlideOutContent,
} from '../components';
import {
  selectAppliedInferenceSet,
  selectClient,
  selectInferenceSet,
  selectIntakeSurvey,
} from '../model';
import { isInferenceEdited, possessivePronoun } from '../util';
import moduleStyles from './OnboardingOurPredictions.module.css';
import mainStyles from '../styles/main.module.css';
import { SlideContent, SlideOutSheet } from '../components';

import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { Badge } from '../components/Badge';

const styles = {
  ...mainStyles,
  ...moduleStyles,
};

export function OnboardingOurPredictions(_props: ClientIdProps) {
  const {
    clientFirstName,
    appliedInferenceSet: { ltcAtAgeShowRange },
  } = useSelector(selectClient);

  return (
    <OnboardingChrome
      slidePage={OnboardingOurPredictions}
      nextButtonLabel={`${clientFirstName}'s Care Needs`}
    >
      <SlideContent headerText={`Our predictions for ${clientFirstName}`}>
        <div className="flex flex-col gap-4 pb-6 pt-6 lg:flex-row lg:gap-5 lg:pb-12 lg:pt-12">
          <ColumnLtcLikelihoodEver />
          <ColumnLtcAtAge />
          <ColumnLtcAgeFallBack />
        </div>
        <div className="py-8 md:py-16">
          <Citations
            citations={
              ltcAtAgeShowRange
                ? [
                    citations.onboardingOurPredictionsLikelihoodNationalAverage,
                    citations.onboardingOurPredictionsLtcAtAgeRange,
                  ]
                : [citations.onboardingOurPredictionsLikelihoodNationalAverage]
            }
          />
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

export function ColumnLtcLikelihoodEver() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  return (
    <div
      className="flex-grow-1 border-primary-200 basis-1/2 rounded-2xl border p-6 text-center"
      data-testid="predictions-likelihood-column"
    >
      <p
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predictions-likelihood-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} likelihood of needing LTC
        <SlideOutSheet
          slideOutContent={<LikelihoodSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-400 hover:text-gray-500"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </p>
      <p
        className="text-primary-dark text-6xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predictions-likelihood-value"
      >
        {Math.round(ltcLikelihoodEver * 100)}
        <span className={styles.percentSymbol}>%</span>
      </p>
      <p
        className="text-base font-normal text-gray-700 md:text-xl"
        data-testid="predictions-likelihood-benchmark"
      >
        U.S. Average at age 65: 56%<sup>1</sup>
      </p>
    </div>
  );
}

export function ColumnLtcAtAge() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcAtAge, ltcAtAgeShowRange } = useSelector(
    selectAppliedInferenceSet,
  );
  const client = useSelector(selectClient);

  if (ltcAtAgeShowRange) {
    return null;
  }

  return (
    <div
      className="flex-grow-1 border-primary-200 basis-1/2 rounded-2xl border p-6 text-center"
      data-testid="predictions-age-column"
    >
      <p
        className="text-base font-medium text-gray-800 md:text-xl"
        data-testid="predictions-age-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} care is expected to begin at
        <SlideOutSheet
          slideOutContent={<LtcAtAgeSlideOutContent />}
          title="AI"
        >
          <BsFillQuestionCircleFill
            className="h-5 w-5 pb-1 pl-1 text-gray-400 hover:text-gray-500"
            aria-hidden="true"
          />
        </SlideOutSheet>
      </p>
      <p
        className="text-primary-dark text-6xl font-semibold leading-[1.5] md:text-9xl"
        data-testid="predictions-age-value"
      >
        {ltcAtAge}
      </p>
      <p className="flex justify-center gap-2 text-base font-normal text-gray-700 md:text-xl">
        <span data-testid="predictions-age-unit">years old</span>
        {isInferenceEdited(client, 'ltcAtAge') && (
          <Badge
            color="gray"
            label="edited"
            className="mt-1"
          />
        )}
      </p>
    </div>
  );
}

function ColumnLtcAgeFallBack() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcAtAge, ltcAtAgeShowRange } = useSelector(
    selectAppliedInferenceSet,
  );
  const client = useSelector(selectClient);

  if (!ltcAtAgeShowRange) {
    return null;
  }

  return (
    <div className="flex-grow-1 basis-1/2 rounded-2xl border border-primary-200 p-6 text-center">
      <p className="text-base font-medium text-gray-800 md:text-xl">
        {startCase(possessivePronoun(intakeSurvey))} care is expected to begin
        at <sup className="font-normal text-gray-600">2</sup>
      </p>
      <p className="text-6xl font-semibold leading-[1.5] text-primary-dark md:text-9xl">
        {ltcAtAge}
      </p>
      <p className="flex justify-center gap-2 text-base font-normal text-gray-700 md:text-xl">
        <span>years old</span>
        {isInferenceEdited(client, 'ltcAtAge') && (
          <Badge
            color="gray"
            label="edited"
            className="mt-1"
          />
        )}
      </p>
    </div>
  );
}
